/**
 * Service to fetch and cache underlying stock prices
 * Used for calculating notional values in options positions
 */

import { quotesCacheService } from "./quotes-cache-service";
import { getApiBaseUrl } from "@/lib/http-utils";

interface QuoteEntry {
  midPrice?: number;
  lastPrice?: number;
  closePrice?: number;
  bidPrice?: number;
  askPrice?: number;
  bidSize?: number;
  askSize?: number;
  volume?: number;
}

interface EquityQuoteResponse {
  data?: Record<string, QuoteEntry>;
}

function getPriceFromQuote(entry: QuoteEntry | undefined): number | null {
  if (!entry) {
    return null;
  }
  const price = entry.midPrice ?? entry.lastPrice ?? entry.closePrice;
  return price && price > 0 ? price : null;
}

export class UnderlyingPriceService {
  private static priceCache = new Map<
    string,
    { price: number; timestamp: number }
  >();
  private static readonly CACHE_DURATION = 60000; // 1 minute

  /**
   * Get the underlying price for a symbol
   * First checks cache, then quotes service, then fetches from API
   */
  static async getUnderlyingPrice(symbol: string): Promise<number | null> {
    // Check cache first
    const cached = this.priceCache.get(symbol);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.price;
    }

    // Check quotes cache for real-time data
    const quote = quotesCacheService.getQuote(symbol);
    if (quote && quote.midPrice) {
      this.priceCache.set(symbol, {
        price: quote.midPrice,
        timestamp: Date.now(),
      });
      return quote.midPrice;
    }

    // If no real-time quote, fetch from REST API
    try {
      const base = getApiBaseUrl();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (typeof window === 'undefined') {
        try {
          const { serverAuthManager } = await import("@/lib/server-auth-manager");
          const token = await serverAuthManager.getSessionToken();
          if (token) headers.Authorization = token;
        } catch {}
      }
      const response = await fetch(
        `${base}/api/market-data/equity-quotes?symbols=${symbol}`,
        {
          headers,
          credentials: "include" as RequestCredentials,
        },
      );

      if (response.ok) {
        const data = (await response.json()) as EquityQuoteResponse;
        const price = getPriceFromQuote(data.data?.[symbol]);
        if (price) {
          this.priceCache.set(symbol, {
            price,
            timestamp: Date.now(),
          });
          console.log(`Fetched price for ${symbol} from API: ${price}`);
          return price;
        }
      }
    } catch (error) {
      console.error(`Failed to fetch underlying price for ${symbol}:`, error);
    }

    return null;
  }

  /**
   * Get prices for multiple symbols at once
   */
  static async getUnderlyingPrices(
    symbols: string[],
  ): Promise<Map<string, number>> {
    const prices = new Map<string, number>();
    const symbolsToFetch: string[] = [];

    // First check cache and quotes service
    for (const symbol of symbols) {
      // Check cache
      const cached = this.priceCache.get(symbol);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        prices.set(symbol, cached.price);
        continue;
      }

      // Check quotes cache
      const quote = quotesCacheService.getQuote(symbol);
      if (quote && quote.midPrice) {
        prices.set(symbol, quote.midPrice);
        this.priceCache.set(symbol, {
          price: quote.midPrice,
          timestamp: Date.now(),
        });
        continue;
      }

      // Need to fetch from API
      symbolsToFetch.push(symbol);
    }

    // Batch fetch remaining symbols from API
    if (symbolsToFetch.length > 0) {
      try {
        // Use credentials: 'include' for cookie-based auth
        const base = getApiBaseUrl();
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (typeof window === 'undefined') {
          try {
            const { serverAuthManager } = await import("@/lib/server-auth-manager");
            const token = await serverAuthManager.getSessionToken();
            if (token) headers.Authorization = token;
          } catch {}
        }
        const response = await fetch(
          `${base}/api/market-data/equity-quotes?symbols=${symbolsToFetch.join(",")}`,
          { 
            headers,
            credentials: 'include' as RequestCredentials,
          },
        );

        if (response.ok) {
          const data = (await response.json()) as EquityQuoteResponse;
          if (data.data) {
            for (const symbol of symbolsToFetch) {
              const quoteData = data.data[symbol];
              const price = getPriceFromQuote(quoteData);
              if (price) {
                prices.set(symbol, price);
                this.priceCache.set(symbol, {
                  price,
                  timestamp: Date.now(),
                });

                if (quoteData) {
                  quotesCacheService.processQuoteUpdate({
                    eventSymbol: symbol,
                    eventType: "Quote",
                    timestamp: Date.now(),
                    bidPrice: quoteData.bidPrice,
                    askPrice: quoteData.askPrice,
                    lastPrice: quoteData.lastPrice,
                    bidSize: quoteData.bidSize,
                    askSize: quoteData.askSize,
                    volume: quoteData.volume,
                  });
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to batch fetch underlying prices:", error);
      }
    }

    return prices;
  }

  /**
   * Get a default price for common stocks when no data is available
   * This is a fallback to prevent $0 notionals
   */
  static getDefaultPrice(symbol: string): number {
    // Common stock default prices (as of late 2024)
    const defaultPrices: Record<string, number> = {
      SPY: 590,
      QQQ: 500,
      AAPL: 250,
      MSFT: 430,
      NVDA: 140,
      TSLA: 400,
      AMZN: 220,
      META: 580,
      GOOGL: 180,
      AMD: 120,
      NFLX: 780,
      JPM: 240,
      BAC: 47,
      XOM: 110,
      CVX: 160,
      WMT: 90,
      HD: 410,
      DIS: 115,
      V: 310,
      MA: 520,
    };

    return defaultPrices[symbol] || 100; // Default to $100 if unknown
  }

  /**
   * Clear the cache
   */
  static clearCache(): void {
    this.priceCache.clear();
  }
}

// Export singleton instance
export const underlyingPriceService = UnderlyingPriceService;
